import {
  Player,
  TrackInfo,
  TrackType,
  AdPlayer,
  RequestType,
  SeekRangeInfo,
  StreamTimelineEvent,
  Action,
  StreamTimelineMetadata,
  ThumbnailPreviewMetadata,
  PlaybackOptions,
  AdComposedPlayer,
  PlayerContext,
  PLAYBACK_STATES,
  TrackVariantInfo,
  AdTechnology,
} from '@quickplay/player-interface';
import { createAdvancedCsaiAdsPlayer } from './AdvancedCsaiAdsPlayer';
import { Error, Logger, createEventEmitter, EventEmitter, AnyFunc, eventObject } from '@quickplay/foundation';
import { PlayerBuilder } from '../../../PlayerBuilder';
import {
  createComposableAFCPlaybackSession,
  emitContentPlayerCurrentState,
  isAdComposedPlayer,
} from '../../util/ComposablePlayerUtils';
import { AdIncomingNotifier, AFCPlaybackSession } from '@quickplay/player';

/**
 * Creates an `AdPlayer` by composing content `Player` and internally created
 * Ad `Player`.
 *
 * The newly created composed `Player` manages the complexities of Ad loading,
 * Ad Management, switching the context between content and ad, etc.
 *
 * @param builder PlayerBuilder Ad PlayerBuilder instance.
 * @returns AdPlayer A new player instance that composes Ad and Content Players
 */
export function createAdvancedCsaiComposableAdPlayer(builder: PlayerBuilder): AdComposedPlayer {
  const logger: Logger = builder.configuredLogger;
  logger.log('[AdvancedCsaiComposableAdPlayer] Initializing Advanced CSAI Composable Ad Player');

  const eventEmitter: EventEmitter = createEventEmitter();
  let afcPlaybackSession: AFCPlaybackSession | undefined;
  let adIncomingNotifier: AdIncomingNotifier | undefined;

  const contentPlayer: Player = builder.player;

  const getAutoPlayPreference = (): boolean => {
    if (contentPlayer.playbackOptions?.autoPlayOnLoad !== undefined) {
      return contentPlayer.playbackOptions.autoPlayOnLoad;
    }

    return true;
  };

  const setAutoPlayOptionToContentPlayer = (autoPlayOnload: boolean): void => {
    if (contentPlayer.playbackOptions) {
      contentPlayer.setPlaybackOptions({
        ...contentPlayer.playbackOptions,
        autoPlayOnLoad: autoPlayOnload,
      });
    }
  };

  const disableAutoPlayOnLoad = (): void => {
    setAutoPlayOptionToContentPlayer(false);
  };

  let autoPlayPreference = getAutoPlayPreference();

  // Note:
  // Disabling autoPlay to stop main content playback during pre-roll ads.
  disableAutoPlayOnLoad();

  // create the `AdPlayer` based on the builder params
  const adPlayer: AdPlayer = createAdvancedCsaiAdsPlayer(builder);

  /**
   * Provides access to the internal player context.
   * @property {PlayerContext} internalPlayerContext
   */
  let internalPlayerContext: PlayerContext = PlayerContext.MAIN;

  let contentCompleted = false;
  let contentStarted = false;

  const loadPlayers = (): void => {
    if (autoPlayPreference) {
      // Note:[QPS-2147] ad player loaded subject to autoPlayOnLoad preferences.
      adPlayer.load();
    }

    // Note: [VCI-8503] Deferring contentPlayer loading until preroll gets completed.
    !builder.deferContentLoad && contentPlayer.load();
  };

  const isAdContext = (): boolean => {
    return internalPlayerContext === PlayerContext.AD;
  };

  /**
   * Executes the content action only when the current context is content.
   */
  const executeForContent = (contentAction: () => void): void => {
    if (!isAdContext()) {
      contentAction();
    }
  };

  /**
   * Executes the action corresponding to the current context.
   */
  const executeByContext = (adAction: () => void, contentAction: () => void): void => {
    (isAdContext() ? adAction : contentAction)();
  };

  /**
   * Returns the ad value while an ad is playing, otherwise computes the content value.
   */
  const getByContext = <T>(adValue: T, contentValue: () => T): T => {
    return isAdContext() ? adValue : contentValue();
  };

  const switchToContext = (context: PlayerContext): void => {
    internalPlayerContext = context;
    logger.info(`Switched to context: ${internalPlayerContext}`);
    eventEmitter.publish('playercontextchanged', internalPlayerContext);
  };

  const enableAutoPlayOnLoad = (): void => {
    setAutoPlayOptionToContentPlayer(true);
  };

  const markContentCompleted = (): void => {
    contentCompleted = true;
  };

  /**
   * Returns the player currently owning playback.
   */
  const getActivePlayer = (): Player => (isAdContext() ? adPlayer : contentPlayer);

  const contentPlayerAction = (state: any): void => {
    if (state) {
      contentPlayer.unsubscribe('streamended', markContentCompleted);
      switchToContext(PlayerContext.AD);
      contentPlayer.pause();
    } else {
      contentPlayer.subscribe('streamended', markContentCompleted);
      switchToContext(PlayerContext.MAIN);

      // Developer Notes:
      // If autoPlayPreference is set true.
      // While switching to main context, if the content player state is still in loading
      // we would enable autoPlay if not call play API on content player because we forcibly
      // disable autoplay before loading both players.
      // Also, we should limit the player not to play if the content is get completed.
      if (autoPlayPreference) {
        if (contentPlayer.playerState === PLAYBACK_STATES.LOADING) {
          enableAutoPlayOnLoad();
        } else if (contentPlayer.playerState === PLAYBACK_STATES.LOADED && !contentCompleted) {
          logger.debug('Ad Player transition after mid roll ads');
          contentPlayer.play();
        } else if (!contentCompleted) {
          contentPlayer.play();
        }
      }

      // Note:
      // When the adPlayer requests resume to the content player. If the autoPlay
      // preference is false and content is not yet started, we need to emit the
      // content player's current state.
      // when the content gets completed and after the end of post-roll ads,
      // content player current state has to be emiited.
      if ((!contentStarted && !autoPlayPreference) || contentCompleted) {
        emitContentPlayerCurrentState(contentPlayer, logger, eventEmitter);
      }
    }
  };

  const subscribeAdPlayer = (player: AdPlayer): void => {
    player.subscribe('adstatechanged', (state: any) => {
      contentPlayerAction(state);
    });
  };

  const subscribeContentPlayer = (player: Player): void => {
    player.subscribe('playbackstatechanged', (state: string) => {
      if (state === PLAYBACK_STATES.STARTED) {
        contentStarted = true;
        contentCompleted = false;
      }

      logger.debug(`Content Player state: ${state}`);
    });
    player.subscribe('streamended', markContentCompleted);
  };

  const composableAdPlayer: AdComposedPlayer = {
    subscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.subscribe(eventName, callback);
      contentPlayer.subscribe(eventName, callback);
      eventEmitter.subscribe(eventName, callback);
    },

    unsubscribe(eventName: string | eventObject, callback?: AnyFunc): void {
      adPlayer.unsubscribe(eventName, callback);
      contentPlayer.unsubscribe(eventName, callback);
      eventEmitter.unsubscribe(eventName, callback);
    },

    get playerContext(): PlayerContext {
      if (!isAdContext() && isAdComposedPlayer(contentPlayer)) {
        return contentPlayer.playerContext;
      }

      return internalPlayerContext;
    },

    get playerState() {
      return getActivePlayer().playerState;
    },

    get playbackRate() {
      return contentPlayer.playbackRate;
    },

    set playbackRate(rate) {
      executeForContent(() => {
        contentPlayer.playbackRate = rate;
      });
    },

    get availableBitrates() {
      return getActivePlayer().availableBitrates;
    },

    set minBitrate(minBitrate) {
      executeForContent(() => {
        contentPlayer.minBitrate = minBitrate;
      });
    },

    get minBitrate() {
      return getActivePlayer().minBitrate;
    },

    set maxBitrate(maxBitrate) {
      executeForContent(() => {
        contentPlayer.maxBitrate = maxBitrate;
      });
    },

    get maxBitrate() {
      return getActivePlayer().maxBitrate;
    },

    get internalPlayerName() {
      return getActivePlayer().internalPlayerName;
    },

    get internalPlayerVersion() {
      return getActivePlayer().internalPlayerVersion;
    },

    get rawPlayer() {
      return getByContext(undefined, () => contentPlayer.rawPlayer);
    },

    get rawLibrary() {
      return getByContext(undefined, () => contentPlayer.rawLibrary);
    },

    get isBuffering() {
      return getActivePlayer().isBuffering;
    },

    get isSeeking() {
      return getActivePlayer().isSeeking;
    },

    get currentTime() {
      return getActivePlayer().currentTime;
    },

    get currentEpochTime() {
      return getActivePlayer().currentEpochTime;
    },

    get duration() {
      return getActivePlayer().duration;
    },

    get playbackStatistics() {
      return getActivePlayer().playbackStatistics;
    },

    get isLive() {
      return contentPlayer.isLive;
    },

    get remainingTime() {
      return getByContext(adPlayer.remainingTime, () => -1);
    },

    get isMute() {
      return getActivePlayer().isMute;
    },

    get playbackOptions() {
      return contentPlayer.playbackOptions ?? undefined;
    },

    get volume() {
      return getActivePlayer().volume;
    },

    set volume(volumeLevel: number) {
      adPlayer.volume = volumeLevel;
      contentPlayer.volume = volumeLevel;
    },

    get adInsertionType(): AdTechnology | undefined {
      return AdTechnology.CSAI;
    },

    load(): void {
      loadPlayers();
    },

    setRequestInterceptor(requestInterceptor: (requestType: RequestType, request: any) => void): void {
      contentPlayer.setRequestInterceptor(requestInterceptor);
    },

    setResponseInterceptor(responseInterceptor: (requestType: RequestType, response: any) => void): void {
      contentPlayer.setResponseInterceptor(responseInterceptor);
    },

    accessFunctionHandler(json: string): void {
      contentPlayer.accessFunctionHandler(json);
    },

    stop(): Promise<void> {
      return adPlayer.stop().finally(() => {
        afcPlaybackSession?.dispose();
        afcPlaybackSession = undefined;
        adIncomingNotifier?.destroy();
        adIncomingNotifier = undefined;
        return contentPlayer.stop();
      });
    },

    play(): void {
      // Reset autoPlayPreference to true if the user call Play API.
      autoPlayPreference = true;

      // When the players are in `idle` state,
      // and if `play` API is triggered, load the ad and contents players
      // Based on ad player state, we will either proceed to play the ad
      // or the content
      if (adPlayer.playerState === 'idle' && contentPlayer.playerState === 'idle') {
        // If the autoPlay is enabled the content player load will override
        // the pause call during the ad playback and plays the main content
        // in the background.
        loadPlayers();
        return;
      } else if (adPlayer.playerState === 'idle') {
        // Note:[QPS-2147] if the autoPlay is disabled on playbackOptions
        // the player on loaded state, upon click on the play button adPlayer
        // will be loaded and play.
        adPlayer.load();
        return;
      }

      executeByContext(
        () => adPlayer.play(),
        () => contentPlayer.play(),
      );
    },

    pause(): void {
      executeByContext(
        () => adPlayer.pause(),
        () => contentPlayer.pause(),
      );
    },

    abort: (error: Error) => {
      adPlayer.abort(error);
      contentPlayer.abort(error);
    },

    send(eventName: StreamTimelineEvent, action: Action, metadata?: StreamTimelineMetadata): void {
      adPlayer.send(eventName, action, metadata);
      contentPlayer.send(eventName, action, metadata);
    },

    seek(seekPoint: number): void {
      adPlayer.seek(seekPoint);
    },

    async getThumbnail(position: number): Promise<ThumbnailPreviewMetadata | undefined> {
      return getActivePlayer().getThumbnail(position);
    },

    seekToLiveEdge(): void {
      getActivePlayer().seekToLiveEdge();
    },

    seekableRange(): SeekRangeInfo {
      return getActivePlayer().seekableRange();
    },

    currentOffsetFromLiveEdge(): number {
      return getActivePlayer().currentOffsetFromLiveEdge();
    },

    getAllTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getAllTracks();
    },

    getActiveTracks(): TrackInfo[] {
      return isAdContext() ? [] : contentPlayer.getActiveTracks();
    },

    getVariantTracks(): TrackVariantInfo[] {
      return contentPlayer.getVariantTracks();
    },

    getAvailableTrackVariants(trackType: TrackType): TrackVariantInfo[] {
      return contentPlayer.getAvailableTrackVariants(trackType);
    },

    selectTrack(track?: TrackInfo): void {
      executeForContent(() => {
        contentPlayer.selectTrack(track);
      });
    },

    muteOrUnmute(): void {
      adPlayer.muteOrUnmute?.();
      contentPlayer.muteOrUnmute?.();
    },

    skipAd(): void {
      executeByContext(
        () => adPlayer.skipAd(),
        () => logger.warn(`Unsupported operation 'skipAd' in ${internalPlayerContext} context`),
      );
    },

    resize(width: number, height: number, isFullscreen: boolean): void {
      adPlayer.resize(width, height, isFullscreen);
    },

    discardAdBreak(): void {
      executeByContext(
        () => adPlayer.discardAdBreak(),
        () => logger.warn(`Unsupported operation 'discardAdBreak' in ${internalPlayerContext} context`),
      );
    },

    getCuePoints(): number[] {
      return adPlayer.getCuePoints();
    },

    setPlaybackOptions(playbackOptions: PlaybackOptions): void {
      contentPlayer.setPlaybackOptions(playbackOptions);
    },

    restrictQuality(enableRestriction: boolean): void {
      contentPlayer.restrictQuality(enableRestriction);
    },

    availableMimeTypes(trackType: TrackType): Array<string> {
      return contentPlayer.availableMimeTypes(trackType);
    },

    setPreferredMimeTypes(trackType: TrackType, mimeTypes: Array<string>): void {
      return contentPlayer.setPreferredMimeTypes(trackType, mimeTypes);
    },

    unloadAndSavePreload(): Promise<void> {
      // no-op
      return Promise.resolve();
    },

    restoreFromPreload(): void {
      // no-op
    },
  };

  subscribeAdPlayer(adPlayer);
  subscribeContentPlayer(contentPlayer);

  afcPlaybackSession = createComposableAFCPlaybackSession(builder, composableAdPlayer, adPlayer);
  if (!composableAdPlayer.isLive) {
    adIncomingNotifier = new AdIncomingNotifier(
      composableAdPlayer,
      eventEmitter,
      builder?.configuredIncomingLeadTimeMs,
      builder?.configuredLogger,
    );
    builder?.configuredLogger?.log('AdIncomingNotifier initialized:');
  }

  return composableAdPlayer;
}
